import { unzip } from './zip.js';

/**
 * The build preview's shell (`docs/admin-plan.md` §D5, *A game's builds*), on the preview's own host.
 *
 * The console frames this page, waits for it to say it is ready, and posts it a web build's original
 * bytes. This page unpacks them into Cache Storage under `/play/run/<id>/`, the service worker
 * (`public/play/sw.js`) serves them from there, and the frame moves to the build's entry page — the
 * one `harness-config.json` names, as a Board's browser would open it. It holds no credential and
 * asks BoardFlight for nothing, so a game running here can reach nothing of the console's.
 *
 * Only the console may send a build: its origin is written into the page by the Worker, and a
 * message from anywhere else, or from a window that is not the one framing this page, is ignored.
 */

const RUN = '/play/run/';
const CACHE_PREFIX = 'bf-run-';

const status = document.getElementById('status')!;
const say = (text: string) => {
  status.textContent = text;
};

const consoleOrigin = document.querySelector<HTMLMetaElement>('meta[name="bf-console"]')?.content ?? '';

/** The worker that serves a run, installed and active; it does not control this page, only `/play/run/`. */
async function worker(): Promise<void> {
  const registration = await navigator.serviceWorker.register('/play/sw.js', { scope: RUN });
  const current = registration.installing ?? registration.waiting ?? registration.active;
  if (!current) throw new Error('The preview’s service worker did not start.');
  // Checked again once the listener is on: a worker can finish activating between `register`
  // resolving and the listener being added, and then no `statechange` ever comes.
  await new Promise<void>((resolve, reject) => {
    const check = () => {
      if (current.state === 'activated') resolve();
      else if (current.state === 'redundant') {
        if (registration.active?.state === 'activated') resolve();
        else reject(new Error('The preview’s service worker did not start.'));
      }
    };
    current.addEventListener('statechange', check);
    check();
  });
}

const TYPES: Record<string, string> = {
  html: 'text/html; charset=utf-8',
  htm: 'text/html; charset=utf-8',
  js: 'text/javascript; charset=utf-8',
  mjs: 'text/javascript; charset=utf-8',
  css: 'text/css; charset=utf-8',
  json: 'application/json',
  map: 'application/json',
  txt: 'text/plain; charset=utf-8',
  xml: 'application/xml',
  svg: 'image/svg+xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  ico: 'image/x-icon',
  wasm: 'application/wasm',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  mp4: 'video/mp4',
  webm: 'video/webm',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  otf: 'font/otf',
  glb: 'model/gltf-binary',
  gltf: 'model/gltf+json',
};

const typeOf = (path: string) => TYPES[path.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';

/** The page `harness-config.json` names, or `index.html` as `web-pack` defaults to. */
function entryOf(files: Map<string, Uint8Array>): string {
  const config = files.get('harness-config.json');
  if (config) {
    try {
      const entry = (JSON.parse(new TextDecoder().decode(config)) as { entry?: unknown }).entry;
      if (typeof entry === 'string' && files.has(entry)) return entry;
    } catch {
      // A manifest that does not parse still leaves the default page to try.
    }
  }
  if (files.has('index.html')) return 'index.html';
  throw new Error('This build has no page to open.');
}

async function run(bytes: ArrayBuffer): Promise<void> {
  say('Unpacking…');
  const files = new Map((await unzip(new Uint8Array(bytes))).map((entry) => [entry.path, entry.bytes]));
  const entry = entryOf(files);

  // One run at a time: whatever an earlier preview left in this browser goes first.
  for (const key of await caches.keys()) if (key.startsWith(CACHE_PREFIX)) await caches.delete(key);

  const id = crypto.randomUUID();
  const base = new URL(`${RUN}${id}/`, location.origin);
  const cache = await caches.open(CACHE_PREFIX + id);
  await Promise.all(
    [...files].map(([path, body]) =>
      cache.put(new URL(path, base).href, new Response(body as BodyInit, { headers: { 'content-type': typeOf(path) } })),
    ),
  );
  location.replace(new URL(entry, base).href);
}

async function start(): Promise<void> {
  if (!consoleOrigin || window.parent === window) {
    say('Open a preview from the console.');
    return;
  }
  if (!('serviceWorker' in navigator) || typeof DecompressionStream === 'undefined') {
    say('This browser cannot run the preview.');
    return;
  }
  await worker();

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== consoleOrigin || event.source !== window.parent) return;
    const message = event.data as { type?: unknown; bytes?: unknown };
    if (message?.type !== 'bf-preview-run' || !(message.bytes instanceof ArrayBuffer)) return;
    run(message.bytes).catch((failure: unknown) => {
      const text = failure instanceof Error ? failure.message : String(failure);
      say(text);
      window.parent.postMessage({ type: 'bf-preview-failed', message: text }, consoleOrigin);
    });
  });
  window.parent.postMessage({ type: 'bf-preview-ready' }, consoleOrigin);
}

start().catch((failure: unknown) => say(failure instanceof Error ? failure.message : String(failure)));
